import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import type {
  Guest,
  GuestAnomaly,
  GuestFilters,
  GuestHistoryEntry,
  GuestListResponse,
  CheckOutRequest,
} from '../types/guest';
import type { CheckInRequest } from '../types/checkin';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const API_PREFIX = '/api/v1/hotel';

function buildAssetUrl(path?: string): string {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
    return path;
  }
  // Append auth token for <img> tags that can't send Authorization headers
  const token = localStorage.getItem('token') || '';
  const sep = path.includes('?') ? '&' : '?';
  return `${API_URL}${path}${token ? sep + 'token=' + token : ''}`;
}

function normalizePoliceFileStatus(status?: string): string {
  return (status || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

function mapPoliceFileStatus(status?: string): Guest['policeFileStatus'] {
  switch (normalizePoliceFileStatus(status)) {
    case 'CLOTUREE':
      return 'Clôturée';
    case 'VALIDEE':
      return 'Validée';
    default:
      return 'Ouverte';
  }
}

function getCurrentUserIdentifier(): string {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      if (typeof user?.email === 'string' && user.email.trim()) return user.email.trim();
      if (typeof user?.name === 'string' && user.name.trim()) return user.name.trim();
      if (typeof user?.id === 'string' && user.id.trim()) return user.id.trim();
    } catch {
      // Ignore malformed user payload and fall through to token parsing.
    }
  }

  const username = localStorage.getItem('username');
  if (username?.trim()) return username.trim();

  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (typeof payload?.email === 'string' && payload.email.trim()) return payload.email.trim();
      if (typeof payload?.sub === 'string' && payload.sub.trim()) return payload.sub.trim();
      if (typeof payload?.name === 'string' && payload.name.trim()) return payload.name.trim();
    } catch {
      // Ignore malformed token payload.
    }
  }

  return 'hotel_user';
}

function humanizeAuditAction(action?: string): string {
  switch ((action || '').toUpperCase()) {
    case 'CREATE':
      return 'Création';
    case 'UPDATE':
      return 'Modification';
    case 'DELETE':
      return 'Suppression';
    case 'STATUS_CHANGE':
      return 'Changement de statut';
    case 'INVOICE_UPLOAD':
      return 'Téléversement de facture';
    case 'API_TRANSMISSION':
      return 'Transmission aux autorités';
    case 'CHECKOUT':
      return 'Check-out';
    default:
      return action || 'Événement';
  }
}

function humanizeFieldName(key: string): string {
  const labels: Record<string, string> = {
    client: 'client',
    name: 'nom',
    gender: 'genre',
    date_of_birth: 'date de naissance',
    nationality: 'nationalité',
    document_type: 'type de document',
    document_number: 'numéro de document',
    document_expiry: 'expiration du document',
    document_photo: 'photo du document',
    photo: 'photo',
    signature: 'signature',
    email: 'email',
    phone_number: 'téléphone',
    stay: 'séjour',
    stay_purpose: 'motif du séjour',
    room_type: 'type de chambre',
    room_number: 'numéro de chambre',
    final_room_number: 'numéro de chambre final',
    arrival_date: 'date d’arrivée',
    checkout_date: 'date de départ réel',
    departure_date: 'date de départ',
    departure_mode: 'mode de départ',
    booking_reference: 'référence de réservation',
    provenance: 'provenance',
    status: 'statut',
    invoice: 'facture',
    notes: 'notes internes',
    amount_type: 'type de montant',
    additional_charges: 'frais supplémentaires',
    damage_report: 'rapport de dommages',
    satisfaction_rating: 'note de satisfaction',
    feedback: 'commentaires client',
    transmission: 'transmission',
  };

  return labels[key] || key.replace(/_/g, ' ');
}

function describeAuditLog(action?: string, changedFields?: Record<string, any>): string {
  const normalizedAction = (action || '').toUpperCase();

  if (normalizedAction === 'STATUS_CHANGE') {
    const previousStatus = changedFields?.status?.old_value;
    const nextStatus = changedFields?.status?.new_value;
    if (previousStatus || nextStatus) {
      return `Statut modifié de ${previousStatus || '-'} vers ${nextStatus || '-'}.`;
    }
    return 'Statut mis à jour.';
  }

  if (normalizedAction === 'INVOICE_UPLOAD') {
    const fileName = changedFields?.invoice?.file_name;
    return fileName ? `Facture téléversée: ${fileName}.` : 'Facture téléversée.';
  }

  if (normalizedAction === 'API_TRANSMISSION') {
    const destination = changedFields?.transmission?.destination;
    const responseStatus = changedFields?.transmission?.response_status;
    if (destination && responseStatus) {
      return `Transmission envoyée vers ${destination} (${responseStatus}).`;
    }
    if (destination) {
      return `Transmission envoyée vers ${destination}.`;
    }
    return 'Transmission API effectuée.';
  }

  if (normalizedAction === 'CHECKOUT') {
    const checkoutDate = changedFields?.checkout_date;
    const roomNumber = changedFields?.final_room_number;
    const departureMode = changedFields?.departure_mode;
    const summary = [checkoutDate, roomNumber, departureMode].filter(Boolean).join(', ');
    return summary ? `Check-out enregistré: ${summary}.` : 'Check-out enregistré.';
  }

  if (normalizedAction === 'CREATE') {
    return 'Fiche créée.';
  }

  if (normalizedAction === 'DELETE') {
    return 'Fiche supprimée.';
  }

  const fieldNames = Object.keys(changedFields || {});
  if (fieldNames.length > 0) {
    return `Champs mis à jour: ${fieldNames.map(humanizeFieldName).join(', ')}.`;
  }

  return normalizedAction === 'UPDATE' ? 'Fiche mise à jour.' : 'Événement enregistré.';
}

function inferAnomalySeverity(daysSinceCheckIn: number): GuestAnomaly['severity'] {
  if (daysSinceCheckIn >= 60) return 'critical';
  if (daysSinceCheckIn >= 45) return 'high';
  if (daysSinceCheckIn >= 30) return 'medium';
  return 'low';
}

function transformGuestAnomaly(data: any): GuestAnomaly {
  const rawResolvedBy = typeof data?.resolved_by === 'string' ? data.resolved_by : '';
  const ignored = rawResolvedBy.startsWith('ignored:');
  const resolvedBy = ignored ? rawResolvedBy.slice('ignored:'.length) : rawResolvedBy;
  const daysSinceCheckIn = Number(data?.days_since_check_in || 0);
  const roomNumber = data?.room_number ? `Chambre ${data.room_number}` : '';
  const elapsed = daysSinceCheckIn > 0 ? `${daysSinceCheckIn} jour${daysSinceCheckIn > 1 ? 's' : ''} depuis l’arrivée` : '';
  const stillPresent = data?.is_guest_still_present ? 'Client encore présent' : '';
  const context = [roomNumber, elapsed, stillPresent].filter(Boolean).join('. ');

  return {
    id: data?.id || '',
    guestId: data?.police_file_id || '',
    type: 'missing_invoice',
    severity: inferAnomalySeverity(daysSinceCheckIn),
    title: data?.reason || 'Anomalie détectée',
    description: context ? `${data?.reason || 'Anomalie détectée'}. ${context}.` : (data?.reason || 'Anomalie détectée'),
    status: data?.resolved_at ? (ignored ? 'ignored' : 'resolved') : 'open',
    createdAt: data?.detected_at || data?.created_at || '',
    resolvedAt: data?.resolved_at || undefined,
    resolvedBy: resolvedBy || undefined,
  };
}

function transformAuditLog(log: any): GuestHistoryEntry {
  return {
    id: log?.id || '',
    guestId: log?.police_file_id || '',
    action: humanizeAuditAction(log?.action),
    description: describeAuditLog(log?.action, log?.changed_fields),
    performedBy: log?.user_id || 'system',
    performedAt: log?.timestamp || log?.created_at || '',
    metadata: log?.changed_fields && Object.keys(log.changed_fields).length > 0 ? log.changed_fields : undefined,
  };
}

// Transform backend response to frontend Guest type
function transformBackendToGuest(data: any): Guest {
  // Parse client_name into firstName and lastName
  const clientName = data.client_name || '';
  const nameParts = clientName.trim().split(/\s+/);
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  // Map status from backend format
  let status: Guest['status'] = 'CHECKED_IN';
  if (data.checkout_date || data.actual_checkout_date) {
    status = 'CHECKED_OUT';
  } else if (normalizePoliceFileStatus(data.status) === 'CLOTUREE') {
    status = 'CHECKED_OUT';
  }

  // Build full URL for photos — served via Nginx proxy to MinIO
  const photoUrl = buildAssetUrl(data.photo_url);
  const signatureUrl = buildAssetUrl(data.signature_url);
  const identityPhotoUrl = buildAssetUrl(data.identity_photo_url);

  return {
    id: data.id,
    firstName,
    lastName,
    gender: data.gender || '',
    dateOfBirth: data.date_of_birth || '',
    nationality: data.nationality || '',
    documentType: data.document_type || '',
    passportNumber: data.document_number || '',
    passportExpiry: data.document_expiry || '',
    email: data.email || '',
    phone: data.phone_number || data.phone || '',
    stayPurpose: data.stay_purpose || '',
    roomType: data.room_type || '',
    provenance: data.provenance || '',
    bookingReference: data.booking_reference || '',
    roomNumber: data.room_number || '',
    checkInDate: data.arrival_date || '',
    checkOutDate: data.departure_date || data.checkout_date || '',
    status,
	amountTTC: data.amount_ttc ?? undefined,
	currency: data.currency || undefined,
    securityScore: data.security_score,
    hasAlerts: data.alert || false,
    alertCount: data.alert ? 1 : 0,
    createdAt: data.created_at || '',
    updatedAt: data.updated_at || '',
    // identity_photo_url is the scanned document, photo_url is the face photo
    documentPhotoUrl: identityPhotoUrl,
    facePhotoBase64: photoUrl,
    signatureBase64: signatureUrl,
    cnsTransmittedAt: data.cns_transmitted_at,
    ontTransmittedAt: data.ont_transmitted_at,
    dgiTransmittedAt: data.dgi_transmitted_at,
    policeFileNumber: data.police_file_number || '',
    policeFileStatus: mapPoliceFileStatus(data.status),
    invoiceUrl: data.invoice_url,
    isTaxExempt: data.is_tax_exempt || false,
    exemptionType: data.exemption_type || '',
  };
}

// Transform backend list response
function transformGuestListResponse(data: any): GuestListResponse {
  const guests = (data.guests || []).map(transformBackendToGuest);
  return {
    guests,
    total: data.total || 0,
    page: data.page || 1,
    pageSize: data.page_size || 20,
  };
}

export const guestService = {
  // Get list of guests with filters
  async getGuests(filters: GuestFilters = {}, page = 1, pageSize = 20): Promise<GuestListResponse> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.roomNumber) params.append('room_number', filters.roomNumber);
    if (filters.roomType) params.append('room_type', filters.roomType);
    if (filters.nationality) params.append('nationality', filters.nationality);
    if (filters.bookingReference) params.append('booking_reference', filters.bookingReference);
    if (filters.checkInFrom) params.append('start_date', filters.checkInFrom);
    if (filters.checkInTo) params.append('end_date', filters.checkInTo);
    if (filters.sortBy) params.append('sort_by', filters.sortBy);
    if (filters.sortOrder) params.append('sort_order', filters.sortOrder);
    if (filters.hasAlerts !== undefined) params.append('hasAlerts', String(filters.hasAlerts));
    params.append('page', String(page));
    params.append('page_size', String(pageSize));

    const response = await api.get(`${API_PREFIX}/guests?${params.toString()}`);
    return transformGuestListResponse(response.data);
  },

  // Get single guest by ID
  async getGuestById(id: string): Promise<Guest> {
    const response = await api.get(`${API_PREFIX}/guests/${id}`);
    return transformBackendToGuest(response.data);
  },

  // Check in a new guest
  async checkIn(data: CheckInRequest): Promise<Guest> {
    try {
      const response = await api.post(`${API_PREFIX}/guests/checkin`, data);
      return response.data;
    } catch (error: any) {
      console.error('Check-in error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        message: error.message,
      });
      throw error;
    }
  },

  // Check out a guest
  async checkOut(data: CheckOutRequest): Promise<void> {
    await api.put(`${API_PREFIX}/guests/${data.guestId}/checkout`, {
      checkout_date: data.checkOutDate,
      final_room_number: data.finalRoomNumber || '',
      notes: data.notes,
      accommodation_amount: data.accommodationAmount || 0,
      amount_type: data.amountType || 'TTC',
      departure_mode: data.departureMode || 'Normal',
      invoice: data.invoice || '',
      invoice_filename: data.invoiceFilename || '',
      additional_charges: data.additionalCharges || 0,
      damage_report: data.damageReport || '',
      satisfaction_rating: data.satisfactionRating || 0,
      feedback: data.feedback || '',
    });
  },

  // Update guest information
  async updateGuest(id: string, data: Partial<Guest>): Promise<Guest> {
    const backendRequest: Record<string, Record<string, unknown>> = {};
    const client: Record<string, unknown> = {};
    const stay: Record<string, unknown> = {};

    if (data.firstName !== undefined || data.lastName !== undefined) {
      client.name = `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim();
    }
    if (data.gender !== undefined) client.gender = data.gender;
    if (data.dateOfBirth !== undefined) client.date_of_birth = data.dateOfBirth;
    if (data.nationality !== undefined) client.nationality = data.nationality;
    if (data.documentType !== undefined) client.document_type = data.documentType;
    if (data.passportNumber !== undefined) client.document_number = data.passportNumber;
    if (data.passportExpiry !== undefined) client.document_expiry = data.passportExpiry;
    if (data.email !== undefined) client.email = data.email;
    if (data.phone !== undefined) client.phone_number = data.phone;
    if (data.facePhotoBase64 !== undefined && data.facePhotoBase64.startsWith('data:')) {
      client.photo = data.facePhotoBase64;
    }
    if (data.signatureBase64 !== undefined && data.signatureBase64.startsWith('data:')) {
      client.signature = data.signatureBase64;
    }
    if (data.documentPhotoUrl !== undefined && data.documentPhotoUrl.startsWith('data:')) {
      client.document_photo = data.documentPhotoUrl;
    }

    if (data.roomType !== undefined) stay.room_type = data.roomType;
    if (data.roomNumber !== undefined) stay.room_number = data.roomNumber;
    if (data.checkInDate !== undefined) stay.arrival_date = data.checkInDate;
    if (data.checkOutDate !== undefined) stay.departure_date = data.checkOutDate;
    if (data.provenance !== undefined) stay.provenance = data.provenance;
    if (data.stayPurpose !== undefined) stay.stay_purpose = data.stayPurpose;
    if (data.bookingReference !== undefined) stay.booking_reference = data.bookingReference;

    if (Object.keys(client).length > 0) backendRequest.client = client;
    if (Object.keys(stay).length > 0) backendRequest.stay = stay;
    if (Object.keys(backendRequest).length === 0) {
      return guestService.getGuest(id);
    }

    await api.put(`${API_PREFIX}/guests/${id}`, backendRequest);
    return guestService.getGuest(id);
  },

  // Get guest statistics
  async getStatistics(): Promise<{
    totalGuests: number;
    checkedIn: number;
    checkedOut: number;
    reserved: number;
    withAlerts: number;
  }> {
    const response = await api.get(`${API_PREFIX}/guests/statistics`);
    return response.data;
  },

  // Export guests to Excel
  async exportToExcel(filters: GuestFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.roomNumber) params.append('room_number', filters.roomNumber);
    if (filters.roomType) params.append('room_type', filters.roomType);
    if (filters.nationality) params.append('nationality', filters.nationality);
    if (filters.bookingReference) params.append('booking_reference', filters.bookingReference);
    if (filters.checkInFrom) params.append('start_date', filters.checkInFrom);
    if (filters.checkInTo) params.append('end_date', filters.checkInTo);
    if (filters.sortBy) params.append('sort_by', filters.sortBy);
    if (filters.sortOrder) params.append('sort_order', filters.sortOrder);

    const response = await api.get(`${API_PREFIX}/guests/export?${params.toString()}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  // Get guest by ID
  async getGuest(id: string): Promise<Guest> {
    const response = await api.get(`${API_PREFIX}/guests/${id}`);
    return transformBackendToGuest(response.data);
  },

  // Get guest anomalies
  async getGuestAnomalies(id: string): Promise<GuestAnomaly[]> {
    try {
      const response = await api.get(`${API_PREFIX}/guests/${id}/anomalies`);
      const anomalies = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.anomalies)
          ? response.data.anomalies
          : [];
      return anomalies.map(transformGuestAnomaly);
    } catch {
      // Anomalies endpoint may not exist yet - return empty array gracefully
      return [];
    }
  },

  // Get guest history
  async getGuestHistory(id: string): Promise<GuestHistoryEntry[]> {
    try {
      const response = await api.get(`${API_PREFIX}/guests/${id}/audit`);
      const auditLogs = Array.isArray(response.data?.audit_logs) ? response.data.audit_logs : [];
      return auditLogs.map(transformAuditLog);
    } catch {
      // Audit trail endpoint may not exist yet - return empty array gracefully
      return [];
    }
  },

  // Resolve anomaly
  async resolveAnomaly(_guestId: string, anomalyId: string, resolution: string): Promise<void> {
    const ignored = normalizePoliceFileStatus(resolution).startsWith('[IGNOREE]');
    await api.put(`${API_PREFIX}/anomalies/${anomalyId}/resolve`, {
      resolved_by: ignored ? `ignored:${getCurrentUserIdentifier()}` : getCurrentUserIdentifier(),
    });
  },
};
